// M40 — Feedback screenshot capture.
//
// Turns the page the user is currently looking at into a JPEG blob that
// rides along with their feedback submission, so the maintainer sees
// what they saw. The feedback dialog calls captureScreenshot() when it
// opens, previews the result, and lets the user opt out before sending.
//
// Why #root specifically: the feedback dialog is a MUI <Dialog>, which
// portals into <body> as a *sibling* of <div id="root">. Capturing
// #root therefore grabs the app page underneath the dialog — never the
// dialog itself or its backdrop.
//
// html2canvas is imported dynamically so its weight never lands in the
// initial bundle; the chunk is fetched only when the feedback dialog
// opens.

import { FEEDBACK_SCREENSHOT_MAX_BYTES } from './feedback';

/** Longest-edge cap, in pixels, for the captured image. A feedback
 *  screenshot only needs to be legible to the maintainer — downscaling
 *  past this trims the upload without hurting readability. */
const MAX_DIMENSION = 1600;

/** JPEG quality steps, tried in order until the encoded blob fits under
 *  the byte cap. A real viewport screenshot almost always clears the cap
 *  on the first step; the rest are insurance. */
const QUALITY_STEPS = [0.82, 0.65, 0.5, 0.38];

export interface CapturedScreenshot {
  /** JPEG blob, guaranteed at or under FEEDBACK_SCREENSHOT_MAX_BYTES. */
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Capture the current app page as a JPEG blob. Best-effort: returns
 * null on any failure (no #root, an html2canvas throw, a browser with
 * no canvas.toBlob, or a blob that won't fit under the size cap).
 * Feedback always sends, with or without a screenshot.
 */
export async function captureScreenshot(): Promise<CapturedScreenshot | null> {
  try {
    if (typeof document === 'undefined') return null;
    const target = document.getElementById('root');
    if (!target) return null;

    const { default: html2canvas } = await import('html2canvas');

    const canvas = await html2canvas(target, {
      // JPEG has no alpha — fill transparent regions with the page
      // background so they don't encode as black.
      backgroundColor: readBackgroundColor(),
      // 1 CSS px : 1 image px. html2canvas otherwise multiplies by
      // devicePixelRatio (2-3x on retina), bloating the byte count for
      // no readability gain on a feedback screenshot.
      scale: 1,
      logging: false,
      useCORS: true,
    });

    const sized = downscaleToMax(canvas, MAX_DIMENSION);
    const blob = await encodeUnderCap(sized, FEEDBACK_SCREENSHOT_MAX_BYTES);
    if (!blob) return null;

    return { blob, width: sized.width, height: sized.height };
  } catch {
    // Any failure → no screenshot. Capture must never break feedback.
    return null;
  }
}

/** The page's effective background color, so JPEG flattening matches
 *  what the user sees (dark theme → dark fill). Falls back to white. */
function readBackgroundColor(): string {
  try {
    const bg = getComputedStyle(document.body).backgroundColor;
    if (!bg || bg === 'transparent' || bg.startsWith('rgba(0, 0, 0, 0')) {
      return '#ffffff';
    }
    return bg;
  } catch {
    return '#ffffff';
  }
}

/** Draw `canvas` onto a smaller one when it exceeds `max` on its longest
 *  edge; return it untouched when it already fits. */
function downscaleToMax(canvas: HTMLCanvasElement, max: number): HTMLCanvasElement {
  const longest = Math.max(canvas.width, canvas.height);
  return longest <= max ? canvas : scaleCanvas(canvas, max / longest);
}

function scaleCanvas(canvas: HTMLCanvasElement, factor: number): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(canvas.width * factor));
  out.height = Math.max(1, Math.round(canvas.height * factor));
  const ctx = out.getContext('2d');
  if (!ctx) return canvas;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, out.width, out.height);
  return out;
}

/** Encode `canvas` as JPEG, stepping quality down until the blob fits
 *  under `maxBytes`; a hard 60% downscale is the last resort. Returns
 *  null when nothing fits — the caller treats that as "no screenshot." */
async function encodeUnderCap(
  canvas: HTMLCanvasElement,
  maxBytes: number,
): Promise<Blob | null> {
  for (const quality of QUALITY_STEPS) {
    const blob = await toJpegBlob(canvas, quality);
    if (blob && blob.size <= maxBytes) return blob;
  }
  const smaller = scaleCanvas(canvas, 0.6);
  const last = await toJpegBlob(smaller, 0.5);
  return last && last.size <= maxBytes ? last : null;
}

/** Promise wrapper around HTMLCanvasElement.toBlob for image/jpeg. */
function toJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    } catch {
      resolve(null);
    }
  });
}
